import React, { useEffect, useRef, useState } from 'react';
import './taskCard.css';

export type TaskStatus = 'pending' | 'processing' | 'succeeded' | 'failed' | string;

export interface TaskCardProps {
  status: TaskStatus;
  statusLabel: string;
  timeStr: string;
  model?: string;
  /** 用户自定义任务名（保存在本地，与 task_id 对应）；为空时显示「未命名」 */
  taskName?: string;
  /** 提交新任务名（去首尾空格后）；空串表示恢复为「未命名」 */
  onRenameTask?: (name: string) => void;
  duration?: number;
  error?: string | null;
  videoSrc?: string | null;
  isProcessing?: boolean;
  onDownload?: (url: string) => void;
  /** 点击中间预览区播放 */
  onPlay?: (url: string) => void;
  onDetail?: () => void;
  /** 任务列表 task_id（与 IndexedDB 映射键一致） */
  sourceTaskId?: string;
  /** 擦除字幕：提交字幕擦除任务（url + 任务列表 task_id） */
  onEraseSubtitle?: (url: string, sourceTaskId: string) => void;
  /** 擦除字幕：查询结果并下载（任务列表 task_id） */
  onEraseSubtitleResult?: (sourceTaskId: string) => void;
  /** IndexedDB 中已有擦除字幕记录时展示「擦除字幕结果」 */
  hasEraseSubtitleRecord?: boolean;
  /** 擦除字幕提交进行中（按任务列表 task_id） */
  eraseSubtitleBusyTaskId?: string | null;
  /** 擦除字幕结果查询进行中（按任务列表 task_id） */
  eraseSubtitleResultBusyTaskId?: string | null;
  /** 无视频区时展示的说明（如链接已过期） */
  mediaHint?: string | null;
  className?: string;
}

/** 专门用于任务列表的卡片组件 */
export const TaskCard: React.FC<TaskCardProps> = ({
  status,
  statusLabel,
  timeStr,
  model,
  taskName,
  onRenameTask,
  duration,
  error,
  videoSrc,
  isProcessing,
  onDownload,
  onPlay,
  onDetail,
  sourceTaskId,
  onEraseSubtitle,
  onEraseSubtitleResult,
  hasEraseSubtitleRecord,
  eraseSubtitleBusyTaskId,
  eraseSubtitleResultBusyTaskId,
  mediaHint,
  className = '',
}) => {
  const displayName = (taskName || '').trim() || '未命名';
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(taskName || '');
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraftName(taskName || '');
  }, [taskName, editing]);

  useEffect(() => {
    if (editing) {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }
  }, [editing]);

  const commitName = () => {
    setEditing(false);
    const next = draftName.trim();
    if (next !== (taskName || '').trim()) onRenameTask?.(next);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraftName(taskName || '');
  };

  return (
    <div className={`task-card ${className}`}>
      <div className="task-top">
        <div className="task-top-row">
          <span className="task-top-time">📅 {timeStr}</span>
          <span className={`task-badge ${status}`}>{statusLabel}</span>
        </div>
        <div className="task-top-meta">
          {editing ? (
            <span className="task-name-edit">
              <input
                ref={nameInputRef}
                className="task-name-input"
                value={draftName}
                maxLength={60}
                placeholder="未命名"
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitName();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEdit();
                  }
                }}
                onBlur={commitName}
              />
            </span>
          ) : (
            <span className="task-name-display">
              <span
                className={`task-name-text${(taskName || '').trim() ? '' : ' is-empty'}`}
                title={displayName}
              >
                {displayName}
              </span>
              {onRenameTask ? (
                <button
                  type="button"
                  className="task-name-edit-btn"
                  onClick={() => setEditing(true)}
                  aria-label="编辑任务名"
                  title="编辑任务名"
                >
                  ✏️
                </button>
              ) : null}
            </span>
          )}
          {duration ? <span>⏱ {duration}秒</span> : null}
        </div>
      </div>

      {error && (
        <div className="task-error">
          错误: {error}
        </div>
      )}

      {videoSrc ? (
        <>
          <button
            type="button"
            className="task-video-play"
            onClick={() => onPlay?.(videoSrc)}
            aria-label="播放视频"
          >
            <video className="task-video" src={videoSrc} muted playsInline preload="metadata" />
            <span className="task-video-play-hint" aria-hidden>
              ▶
            </span>
          </button>
          <div className="task-btns">
            <button
              type="button"
              className="task-btn dl"
              onClick={() => onDownload?.(videoSrc)}
            >
              ⬇ 下载
            </button>
            {hasEraseSubtitleRecord && onEraseSubtitleResult && sourceTaskId ? (
              <button
                type="button"
                className="task-btn erase-result"
                onClick={() => onEraseSubtitleResult(sourceTaskId)}
                disabled={eraseSubtitleResultBusyTaskId === sourceTaskId}
              >
                {eraseSubtitleResultBusyTaskId === sourceTaskId ? '查询中…' : '⬇ 擦除结果'}
              </button>
            ) : onEraseSubtitle && sourceTaskId ? (
              <button
                type="button"
                className="task-btn erase"
                onClick={() => onEraseSubtitle(videoSrc, sourceTaskId)}
                disabled={eraseSubtitleBusyTaskId === sourceTaskId}
              >
                {eraseSubtitleBusyTaskId === sourceTaskId ? '提交中…' : '✦ 擦除字幕'}
              </button>
            ) : null}
            {onDetail ? (
              <button type="button" className="task-btn rt" onClick={onDetail}>
                详情
              </button>
            ) : null}
          </div>
        </>
      ) : isProcessing ? (
        <div className="task-loading">
          <div className="spinner" />
          <span>{status === 'pending' ? '排队中...' : '生成中，请稍候...'}</span>
        </div>
      ) : mediaHint ? (
        <div className="task-media-hint">{mediaHint}</div>
      ) : null}

      {!videoSrc && !isProcessing && onDetail ? (
        <div className="task-btns">
          <button type="button" className="task-btn rt" onClick={onDetail}>
            详情
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default TaskCard;
